import React, { useEffect, useState, Suspense } from "react";
import { motion } from "framer-motion";
import { BrowserRouter as Router, Routes, Route, useLocation } from "react-router-dom";
import "./styles/landing/Landing.css";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ToastProvider } from "./context/ToastContext";
import { CartProvider } from "./context/CartContext";
import { ProductProvider } from "./context/ProductContext";
import { LanguageProvider } from "./context/LanguageContext";
import ProtectedRoute from "./components/ProtectedRoute";
import ProtectedAdminRoute from "./components/ProtectedAdminRoute";
import LoadingSpinner from "./components/common/LoadingSpinner";
import ErrorBoundary from "./components/common/ErrorBoundary";
import MainHeader from "./components/layout/MainHeader";
import CookieConsent from "./components/common/CookieConsent";
import PageTransition from "./components/animations/PageTransition";
import { setupGlobalErrorHandlers } from "./utils/errorHandlers";
import { initializeErrorLogger } from "./services/errorLogger";
import { routeConfig } from "./routes/config";
import { ThemeProvider } from "./context/ThemeContext";

// Lazy load heavy components that aren't needed for initial render
const CustomerServiceBot = React.lazy(() => import("./components/chat/CustomerServiceBot"));
const WelcomeAnimation = React.lazy(() => import("./components/common/WelcomeAnimation"));

const LavvissaLanding = () => {
  const [animationState, setAnimationState] = useState("initial");
  const [showChatbot, setShowChatbot] = useState(false);

  useEffect(() => {
    const firstTextTimeout = setTimeout(() => {
      setAnimationState("firstText");
    }, 300);

    const secondTextTimeout = setTimeout(() => {
      setAnimationState("secondText");
    }, 1800);

    const creditTextTimeout = setTimeout(() => {
      setAnimationState("creditText");
    }, 3300);

    const finalTextTimeout = setTimeout(() => {
      setAnimationState("finalText");
    }, 4800);

    const navbarTimeout = setTimeout(() => {
      setAnimationState("navbarVisible");
    }, 6500);

    return () => {
      clearTimeout(firstTextTimeout);
      clearTimeout(secondTextTimeout);
      clearTimeout(creditTextTimeout);
      clearTimeout(finalTextTimeout);
      clearTimeout(navbarTimeout);
    };
  }, []);

  useEffect(() => {
    // Load images lazily
    const lazyLoadImages = () => {
      const imageObserver = new IntersectionObserver((entries, observer) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const img = entry.target;
            const src = img.getAttribute("data-src");
            if (src) {
              img.style.backgroundImage = `url(${src})`;
              img.removeAttribute("data-src");
              observer.unobserve(img);
            }
          }
        });
      });

      document.querySelectorAll(".pick-image[data-src]").forEach((img) => {
        imageObserver.observe(img);
      });
    };

    // Check if IntersectionObserver is available
    if ("IntersectionObserver" in window) {
      lazyLoadImages();


    }
  }, []);

  // Smart reveal for chatbot: after intro, or after user scrolls some, or after 8s fallback
  useEffect(() => {
    if (animationState === "finalText") {
      setShowChatbot(true);
      return;
    }
    const onScroll = () => {
      if (window.scrollY > 240) setShowChatbot(true);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    const t = setTimeout(() => setShowChatbot(true), 8000);
    return () => { window.removeEventListener('scroll', onScroll); clearTimeout(t); };
  }, [animationState]);

  // Removed dynamic header tone switching; header is always black now
  // useEffect(() => {
  //   if (animationState === "finalText") {
  //     const headerEl = document.querySelector('.header');
  //     if (!headerEl) return;
  //     // ... removed logic ...
  //   }
  // }, [animationState]);

  return (
    <motion.div
      key="home"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
    >
      <LanguageProvider>
        <AuthProvider>
          <ToastProvider>
            <ProductProvider>
              <CartProvider>
                <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
                  <AppShell animationState={animationState} showChatbot={showChatbot} />
                </Router>
              </CartProvider>
            </ProductProvider>
          </ToastProvider>
        </AuthProvider>
      </LanguageProvider>
    </motion.div>
  );
};

// Shell under Router so we can use useLocation safely
const AppShell = ({ animationState, showChatbot }) => {
  const location = useLocation();
  const { currentUser, showWelcomeAnimation, setShowWelcomeAnimation } = useAuth();
  const isAuthRoute = location.pathname === "/login" || location.pathname === "/forgot-password";
  const isWalletRoute = location.pathname === "/wallet";
  const isProfileRoute = location.pathname === "/profile";
  const isAdminRoute = location.pathname.startsWith("/admin");
  const isShopRoute = location.pathname.startsWith("/shop");
  const isPolicyRoute = location.pathname.startsWith("/privacy") || 
                        location.pathname.startsWith("/terms") || 
                        location.pathname.startsWith("/cookies") || 
                        location.pathname.startsWith("/refund") || 
                        location.pathname.startsWith("/shipping") || 
                        location.pathname.startsWith("/acceptable-use");
  const isHomeRoute = location.pathname === "/";
  
  // Log route changes for debugging (optional, can be removed in production)
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('Route changed to:', location.pathname);
    }
  }, [location.pathname]);
  
  const renderRouteElement = (Component, route) => {
    const element =
      route.path === "/"
        ? <Component animationState={animationState} isAuthRoute={isAuthRoute} />
        : <Component />;

    if (route.requiresAdmin) return <ProtectedAdminRoute>{element}</ProtectedAdminRoute>;
    if (route.requiresAuth) return <ProtectedRoute>{element}</ProtectedRoute>;
    return element;
  };

  // Ensure global styles toggle correctly for admin pages as a failsafe
  useEffect(() => {
    const body = document.body;
    if (!body) return;
    if (isAdminRoute) {
      body.classList.add("is-admin-route");
    } else {
      body.classList.remove("is-admin-route");
    }
    return () => body.classList.remove("is-admin-route");
  }, [isAdminRoute]);
  return (
    <>
      <div className={`landing-container ${isAuthRoute ? 'auth-route' : ''} ${isWalletRoute ? 'wallet-route' : ''} ${isProfileRoute ? 'profile-route' : ''} ${isShopRoute ? 'shop-route' : ''} ${isPolicyRoute ? 'policy-route' : ''}`}>
        {/* Global Header for non-admin pages - Hidden during initial animation */}
        {!isShopRoute && !isAdminRoute && <MainHeader animationState={isPolicyRoute ? "navbarVisible" : animationState} />}

        <PageTransition>
          <ErrorBoundary>
            <Routes location={location} key={location.pathname}>
              {routeConfig.map((route) => {
                const Component = route.component;
                return (
                  <Route
                    key={route.path}
                    path={route.path}
                    element={
                      <Suspense fallback={<LoadingSpinner size="large" fullPage />}>
                        {renderRouteElement(Component, route)}
                      </Suspense>
                    }
                  />
                );
              })}
            </Routes>
          </ErrorBoundary>
        </PageTransition>
      </div>
      {/* The chatbot is owned here so fixed positioning is never trapped by page transforms. */}
      {!isAdminRoute && !isPolicyRoute && showChatbot && (
        <Suspense fallback={null}>
          <CustomerServiceBot />
        </Suspense>
      )}

      {/* Welcome animation for logged-in users on home page - ONLY after loading is complete */}
      {currentUser && isHomeRoute && showWelcomeAnimation && animationState === "finalText" && (
        <Suspense fallback={null}>
          <WelcomeAnimation
            user={currentUser}
            onComplete={() => setShowWelcomeAnimation(false)}
          />
        </Suspense>
      )}

      {/* Cookie Consent Banner */}
      {!isAdminRoute && <CookieConsent />}
    </>
  );
};

// Public storefront. Account and admin routes retain their own protection.
const App = () => {
  // Initialize error handling on app mount
  useEffect(() => {
    // Setup global error handlers
    setupGlobalErrorHandlers();
    
    // Initialize error logger (processes queued errors, sets up online/offline listeners)
    initializeErrorLogger();
  }, []);

  return (
    <ThemeProvider>
      <LavvissaLanding />
    </ThemeProvider>
  );
};

export default App;
